import { Container } from 'pixi.js';
import type { BlockerView } from '../../types';
import { BirdHouseBlockerLayout } from '../../../../../layouts/blockers/BirdHouseBlockerLayout';
import type { BirdHouseBlockerModel } from '../../models/blockers/BirdHouseBlockerModel';
import { applyLayoutState } from '../../../../libs/layouts/populateLayouts/applyLayoutState';

export class BirdHouseBlockerView extends Container implements BlockerView {
  private readonly layout = new BirdHouseBlockerLayout();

  private readonly initialPositions = {
    birdHouse: this.layout.birdHouse.position.clone(),
    rightDoor: this.layout.rightDoor.position.clone(),
    leftDoor: this.layout.leftDoor.position.clone(),
  };

  constructor(private readonly model: BirdHouseBlockerModel) {
    super();

    const {
      layout,
      layout: { root, birdHead },
    } = this;

    root.props({ parent: this });
    applyLayoutState(layout, 'doors-opened');

    birdHead.props({
      alpha: 0,
    });

    const onStartReleaseAnimation = (): void => {
      void this.animateBirdHouseJump();
    };

    const onStartFullAnimation = async (): Promise<void> => {
      await this.animateBirdHouseShake();
      void this.animateDoorsOpening(300);
      await this.animateBirdHeadPopIn(700);
      await this.animateDoorsClosing(0);
    };

    const onTaskComplete = (): void => {
      void this.animateDoorsClosing(0);
    };

    model.onStartFullAnimation.connect(onStartFullAnimation);
    model.onStartReleaseAnimation.connect(onStartReleaseAnimation);
    model.onTaskComplete.connect(onTaskComplete);

    this.once('destroyed', () => {
      model.onStartFullAnimation.disconnect(onStartFullAnimation);
      model.onStartReleaseAnimation.disconnect(onStartReleaseAnimation);
      model.onTaskComplete.disconnect(onTaskComplete);
    });
  }

  public getModel(): BirdHouseBlockerModel {
    return this.model;
  }

  private async animateBirdHouseJump(): Promise<void> {
    const {
      layout: { birdHouse, whiteOverlay },
      initialPositions,
    } = this;

    whiteOverlay.tweenTo({ alpha: 1 }, 200, 'quadOut', true, 0, 0, true);

    await birdHouse.tweenTo({ y: initialPositions.birdHouse.y - 20 }, 200, 'cubicOut', true).asyncOnComplete();
    await birdHouse.tweenTo({ y: initialPositions.birdHouse.y }, 200, 'cubicIn', true).asyncOnComplete();
  }

  private async animateBirdHouseShake(): Promise<void> {
    const {
      layout: { birdHouse },
      initialPositions,
    } = this;

    const animationData = { animationTime: 0, shakeIntensity: 8 };

    const shakeTween = birdHouse
      .tweenCreate(animationData)
      .to({ animationTime: 1, shakeIntensity: 0 }, 800, 'quadOut', true);
    shakeTween.onUpdate.add(() => {
      birdHouse.x =
        initialPositions.birdHouse.x + Math.sin(animationData.animationTime * 20) * animationData.shakeIntensity;
    });

    await shakeTween.asyncOnComplete();
    birdHouse.x = initialPositions.birdHouse.x;
  }

  private async animateDoorsOpening(delay: number): Promise<void> {
    const {
      layout: { rightDoor, leftDoor, doorHandle },
      initialPositions,
    } = this;

    await Promise.all([
      doorHandle.tweenTo({ angle: -90 }, 150, 'quadIn', true, delay).asyncOnComplete(),
      rightDoor.tweenTo({ x: initialPositions.rightDoor.x + 15 }, 300, 'quadOut', true, delay + 200).asyncOnComplete(),
      leftDoor.tweenTo({ x: initialPositions.leftDoor.x - 15 }, 300, 'quadOut', true, delay + 200).asyncOnComplete(),
    ]);
  }

  private async animateDoorsClosing(delay: number): Promise<void> {
    const {
      layout: { rightDoor, leftDoor, doorHandle },
      initialPositions,
    } = this;

    await Promise.all([
      rightDoor.tweenTo({ x: initialPositions.rightDoor.x }, 300, 'quadOut', true, delay).asyncOnComplete(),
      leftDoor.tweenTo({ x: initialPositions.leftDoor.x }, 300, 'quadOut', true, delay).asyncOnComplete(),
      doorHandle.tweenTo({ angle: 0 }, 150, 'quadIn', true, delay + 200).asyncOnComplete(),
    ]);
  }

  private async animateBirdHeadPopIn(delay: number): Promise<void> {
    const {
      layout: { birdHead },
    } = this;

    birdHead.props({
      scale: 0,
      alpha: 0,
    });

    await birdHead.tweenTo({ scaleY: 1, scaleX: -1, alpha: 1 }, 300, 'backOut', true, delay).asyncOnComplete();
    await birdHead.tweenTo({ scaleY: 0, scaleX: 0, alpha: 0 }, 500, 'backOut', true, delay + 700).asyncOnComplete();
  }

  public async animateRemoval(): Promise<void> {
    this.destroy({ children: true });
  }
}
